import { computed, inject, Injectable, signal } from '@angular/core';
import { MessageService } from 'primeng/api';
import { catchError, defer, map, Observable, of, switchMap, take } from 'rxjs';
import { _, TranslateService } from '@ngx-translate/core';
import { IEvent } from '@app/api/interfaces/event.interface';
import { IPosition } from '@app/core/interfaces/position.interface';
import { MessageConfigsService } from '@app/core/services/message-configs/message-configs.service';
import { ToastSeverity } from '@app/shared/components/toast-content/toast-content.component';
import { DialogEventPersistenceService } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event-persistence.service';
import { DialogTrackerService } from '@app/core/services/dialog-tracker/dialog-tracker.service';
import { AddressesApiService } from '@app/api/services/addresses-api.service';
import {
  DialogSaveEventPositionComponent,
  DialogSaveEventPositionResult,
} from '@app/main/leak-detection/dialogs/dialog-save-event-position/dialog-save-event-position.component';

@Injectable({ providedIn: 'root' })
export class LeakDetectionEventPositionService {
  private readonly persistence = inject(DialogEventPersistenceService);
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly messageConfigsService = inject(MessageConfigsService);
  private readonly dialogTrackerService = inject(DialogTrackerService);
  private readonly addressesApi = inject(AddressesApiService);

  private readonly _event = signal<IEvent | null>(null);
  private readonly _position = signal<IPosition | null>(null);

  /** the spots picked before the current one, for `Undo` — `null` is the event's own position */
  private readonly history = signal<(IPosition | null)[]>([]);

  public readonly event = this._event.asReadonly();
  public readonly position = this._position.asReadonly();
  public readonly isActive = computed(() => !!this._event());
  public readonly hasChanges = computed(() => !!this._position());
  public readonly isSaving = signal(false);

  /** moving another event first asks what happens to this one's unsaved spot — the same event goes on as it is */
  public requestStart(event: IEvent): void {
    if (this._event()?.id === event.id) return;

    this.canLeave()
      .pipe(take(1))
      .subscribe(left => {
        if (left) this.start(event);
      });
  }

  public pick(position: IPosition): void {
    if (!this._event() || this.isSaving()) return;

    this.history.update(history => [...history, this._position()]);
    this._position.set(position);
  }

  /** one pick back — undoing the first one puts the event back on its own position */
  public undo(): void {
    const history = this.history();

    if (!history.length || this.isSaving()) return;

    this.history.set(history.slice(0, -1));
    this._position.set(history.at(-1) ?? null);
  }

  public exit(): void {
    this._event.set(null);
    this._position.set(null);
    this.history.set([]);
    this.isSaving.set(false);
  }

  public requestExit(): void {
    this.canLeave().pipe(take(1)).subscribe();
  }

  // need to return observable boolean for router guard
  public canLeave(): Observable<boolean> {
    return defer(() => {
      if (!this.hasChanges()) {
        this.exit();

        return of(true);
      }

      const baseDialogConfig = this.dialogTrackerService.getBaseDialogConfig();

      const ref = this.dialogTrackerService.regAndOpen(DialogSaveEventPositionComponent, {
        ...baseDialogConfig,
        maskStyleClass: `!bg-(--p-neutral-500)/70 backdrop-blur-[3px] ${baseDialogConfig.maskStyleClass || ''}`,
        width: '26rem',
      });

      return ref.onClose.pipe(
        take(1),
        switchMap((result: DialogSaveEventPositionResult | undefined) => {
          switch (result) {
            case DialogSaveEventPositionResult.Save:
              return this.save();
            case DialogSaveEventPositionResult.Discard:
              this.exit();

              return of(true);
            default:
              return of(false);
          }
        })
      );
    });
  }

  public confirm(): void {
    this.save().pipe(take(1)).subscribe();
  }

  private start(event: IEvent): void {
    this._event.set(event);
    this._position.set(null);
    this.history.set([]);
  }

  private save(): Observable<boolean> {
    const event = this._event();
    const coordinates = this._position();

    if (!event || !coordinates || this.isSaving()) return of(false);

    this.isSaving.set(true);

    return this.lookUpAddress(coordinates).pipe(
      // the address follows the event to its new spot — a spot without one clears it (`null`)
      switchMap(location =>
        this.persistence.update(event.id, { coordinates, ...(location !== undefined ? { location } : {}) })
      ),
      map(() => {
        this.exit();
        this.messageService.add({
          ...this.messageConfigsService.getMainContentMessageConfig(),
          severity: ToastSeverity.Success,
          summary: this.translateService.instant(_(`@@leak-detection-event-toast__position-updated`), {
            I18N_VAR_EVENT_NO: event.serialNo,
          }),
        });

        return true;
      }),
      // a failed write keeps the mode open with the picked spot, so it can be saved again
      catchError(() => {
        this.isSaving.set(false);

        return of(false);
      })
    );
  }

  /** `null`: the spot has no address (an empty list); `undefined`: unknown, the lookup failed — the old one stays */
  private lookUpAddress(coordinates: IPosition): Observable<string | null | undefined> {
    return this.addressesApi.getByCoordinates(coordinates).pipe(
      // `||`: the API rejects an empty `location`
      map(response => response.data[0]?.address || null),
      catchError(() => of(undefined))
    );
  }
}
